import React, { useEffect, useRef, useState } from "react";
import { View, Text } from "../../components/shared/Themed";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import {
  Dimensions,
  Image,
  TouchableOpacity,
  StyleSheet,
  ImageSourcePropType,
} from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { ScrollView } from "react-native-gesture-handler";
import { ActivityIndicator } from "react-native-paper";
import { validSignedURL } from "../../utils/validateSignedUrl";
import { headers } from "./utils/deliveryHeaders";

interface OrderItem {
  DocNum?: string;
  DocType?: string;
  ItemCode?: string;
  ProductGroup?: string;
  CustomerCode?: string;
  CustomerRef?: string;
  DeliveryAddress?: number;
  OrderDate?: number;
  RequestDate?: string;
  DelivrdQty?: string;
  OrderQty?: string;
  ShipToCode?: string;
  ShippingType?: string;
  State?: string;
  Warehouse?: string;
  SalesPerson?: string;
  ProcessBy?: string;
  image?: string;
}

interface RouteParams {
  order: OrderItem;
}

interface DetailItem {
  label: string;
  value: string;
  supportingValue?: string;
}

type SpecialLabels = {
  [key: string]: {
    oldLabel: string;
    newLabel: string;
    newValue?: string;
    supportingValue?: string;
  };
};

const DeliveryDetailsScreen = () => {
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const { order } = route.params || { order: {} };
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width
  );
  const navigation = useNavigation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(false);
  const [isDisplayViewOn, setIsDisplayViewOn] = useState(false);

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    // Redirect if order is invalid
    if (!order || typeof order === "string") {
      navigation.goBack();
      return;
    }

    // Track screen size changes
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    // Cleanup on unmount
    return () => subscription?.remove();
  }, [order, navigation]);

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageLoadStart = () => {
    setIsImageLoading(true);
  };

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <AntDesign name="arrowleft" size={24} color="black" />
          </TouchableOpacity>

          <View style={styles.headerTitleContainer}>
            <Text style={styles.headerTitle}>
              {`Item Code: ${order?.ItemCode || ""}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const ProductDetails = () => {
    const excludedKeys = [
      "BaseReference",
      "BatchAttribute1",
      "BatchAttribute2",
      "BatchAttribute3",
      "U_CashCusName",
      "U_CashCusPhone",
      "U_PMX_BAV1",
      "U_PMX_BAV2",
      "U_PMX_BAV3",
      "LastUpdate",
      "OrderQtyPCS",
      "SQRef.",
      "PaymentTerm",
      "RemainingQtyPCS",
    ];

    const orderedKeys = [
      "DocNum",
      "DocType",
      "ItemCode",
      "ProductGroup",
      "CustomerCode",
      "CustomerRef",
      "DeliveryAddress",
      "OrderDate",
      "RequestDate",
      "DelivrdQty",
      "OrderQty",
      "RemainingQty",
      "ShipToCode",
      "ShippingType",
      "State",
      "Warehouse",
      "SalesPerson",
      "ProcessBy",
    ];

    const specialLabels: SpecialLabels = {
      DocType: {
        oldLabel: "Document",
        newLabel: "Document Type",
      },
      ItemCode: {
        oldLabel: "ItemCode",
        newLabel: "Item Code",
      },
      ProductGroup: {
        oldLabel: "ProductGroup",
        newLabel: "Product Group",
      },
      CustomerCode: {
        oldLabel: "CustomerCode",
        newLabel: "Customer Code",
      },
      CustomerRef: {
        oldLabel: "CustomerCode",
        newLabel: "Customer Ref####",
      },
      DeliveryAddress: {
        oldLabel: "DeliveryAddress",
        newLabel: "Delivery Address",
      },
      OrderDate: {
        oldLabel: "OrderDate",
        newLabel: "Order Date",
      },
      RequestDate: {
        oldLabel: "RequestDate",
        newLabel: "Request Date",
      },
      DelivrdQty: {
        oldLabel: "DelivrdQty",
        newLabel: "Delivery Qty",
      },
      OrderQty: {
        oldLabel: "OrderQty",
        newLabel: "Order Qty",
      },
      RemainingQty: {
        oldLabel: "RemainingQty",
        newLabel: "Remaining Qty",
      },
      SalesPerson: {
        oldLabel: "SalesPerson",
        newLabel: "Sales",
      },
      ProcessBy: {
        oldLabel: "ProcessBy",
        newLabel: "Operator",
      },
    };

    const detailItems: DetailItem[] = orderedKeys
      .filter((key) => !excludedKeys.includes(key))
      .map((key) => {
        if (specialLabels[key]) {
          const special = specialLabels[key];
          return {
            label: special.newLabel,
            value: special.newValue || order?.[key]?.toString() || "-",
            supportingValue: special.supportingValue,
          };
        } else {
          return {
            label: headers[key]?.name || key,
            value: order?.[key]?.toString() || "-",
          };
        }
      });

    return (
      <View style={styles.productDetailsContainer}>
        <View style={styles.header}>
          <Text style={styles.title}>{order?.ItemCode || ""}</Text>
          <Text style={styles.subtitle}>{order?.FrgnName || ""}</Text>
        </View>
        <View style={styles.body}>
          {detailItems.map((item, index) => (
            <View key={index} style={styles.detailRow}>
              <Text style={styles.label}>{item.label}:</Text>
              <Text style={styles.value}>{item.value?.toLocaleString()}</Text>
              {item.supportingValue && (
                <Text style={styles.supporting}>
                  ({item.supportingValue?.toLocaleString()})
                </Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const prevItemCodeRef = useRef<any>();

  useEffect(() => {
    if (order.ItemCode && order.ItemCode !== prevItemCodeRef.current) {
      prevItemCodeRef.current = order.ItemCode;
      validSignedURL(
        order.ItemCode,
        "label",
        setIsImageLoading,
        setSelectedImage
      );
    }
  }, [order.ItemCode]);

  const ProductImage = () => {
    return (
      <View style={styles.imageContainer}>
        {isImageLoading && (
          <ActivityIndicator size="large" style={styles.loader} />
        )}
        <Image
          source={{ uri: selectedImage } as ImageSourcePropType}
          style={styles.productImage}
          resizeMode="contain"
        />
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <DrawerHeader />

      <View
        style={[
          styles.contentContainer,
          isMobileView && styles.contentContainerMobile,
        ]}
      >
        {/* Left Side - Order Details */}
        <View
          style={[
            styles.orderDetails,
            isMobileView ? styles.orderDetailsMobile : null,
          ]}
        >
          <View style={styles.detailBox}>
            <ProductDetails />
          </View>
        </View>

        {/* Middle vertical line - only shown in desktop view */}
        {!isMobileView && <View style={styles.verticalLine} />}

        {/* Right Side - Product Image */}
        <View
          style={[
            styles.imageSection,
            isMobileView ? styles.imageSectionMobile : null,
          ]}
        >
          <View style={styles.detailBox}>
            <ProductImage />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "#fff",
  },
  contentContainer: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  contentContainerMobile: {
    flexDirection: "column",
  },
  orderDetails: {
    flex: 1,
    maxWidth: "48%",
  },
  orderDetailsMobile: {
    maxWidth: "100%",
    marginBottom: 16,
  },
  imageSection: {
    flex: 1,
    maxWidth: "48%",
  },
  imageSectionMobile: {
    maxWidth: "100%",
  },
  detailBox: {
    borderRadius: 14,
    padding: 16,
    height: "100%",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  verticalLine: {
    width: 1,
    backgroundColor: "#e3e3e3",
    marginHorizontal: 16,
  },
  drawerHeaderStyle: {
    width: "100%",
    zIndex: 999,
    backgroundColor: "#fff",
    height: 56,
    borderBottomWidth: 1,
    borderColor: "#e0e0e0",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    paddingVertical: 8,
    paddingRight: 16,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontWeight: "700",
    fontSize: 16,
  },
  // Product Details Styles
  productDetailsContainer: {
    flex: 1,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
  },
  body: {
    flex: 1,
  },
  label: {
    fontWeight: "600",
    fontSize: 14,
    flex: 1,
  },
  value: {
    fontSize: 14,
    color: "#333",
    flex: 1,
    textAlign: "right",
  },
  supporting: {
    fontSize: 12,
    color: "#888",
    marginLeft: 8,
  },
  // Image Styles
  imageContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    minHeight: 250,
  },
  productImage: {
    width: "100%",
    height: "100%",
    flex: 1,
    minHeight: 250,
  },
  loader: {
    position: "absolute",
    zIndex: 1,
  },
});

export default DeliveryDetailsScreen;
